import { useRef } from "react";
import QRCode from "react-qr-code";
import { Copy, Download, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "./ui/utils";
import { toast } from "sonner";
import type { CopyFeedbackHandler } from "../hooks/useCopyFeedback";
import { emitCopyFeedback } from "../utils/copyFeedbackBus";

interface QuickPayQRCodeProps {
  url?: string | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  methodLabel?: string;
  helperText?: string;
  qrSize?: number;
  className?: string;
  onCopyFeedback?: CopyFeedbackHandler;
}

export function QuickPayQRCode({
  url,
  loading,
  error,
  onRetry,
  methodLabel,
  helperText = "Scan to pay and view secure bank transfer instructions.",
  qrSize = 180,
  className,
  onCopyFeedback,
}: QuickPayQRCodeProps) {
  const notifyCopyFeedback = onCopyFeedback ?? emitCopyFeedback;
  const qrWrapperRef = useRef<HTMLDivElement | null>(null);

  const copyLink = async () => {
    if (!url) return;
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
        throw new Error("Clipboard unavailable");
      }
      await navigator.clipboard.writeText(url);
      const message = "Quick pay link copied";
      toast.success(message);
      notifyCopyFeedback("success", message);
    } catch {
      const message = "Unable to copy link right now";
      toast.error(message);
      notifyCopyFeedback("error", message);
    }
  };

  const downloadQr = () => {
    if (!url) return;
    try {
      const svg = qrWrapperRef.current?.querySelector("svg");
      if (!svg) {
        throw new Error("QR not ready");
      }
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svg);
      const blob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "quick-pay-qr.svg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    } catch {
      toast.error("Unable to download QR right now");
    }
  };

  return (
    <div className={cn("flex flex-col items-center gap-3 text-center", className)}>
      {loading ? (
        <p className="text-sm text-muted-foreground">Generating quick pay link...</p>
      ) : error ? (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{error}</p>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry} className="inline-flex items-center gap-1">
              <RotateCcw className="h-4 w-4" />
              Try again
            </Button>
          )}
        </div>
      ) : url ? (
        <>
          <div className="bg-white p-3 rounded-xl shadow-sm border" ref={qrWrapperRef}>
            <QRCode value={url} size={qrSize} />
          </div>
          {methodLabel && (
            <p className="text-sm font-medium text-foreground">{methodLabel}</p>
          )}
          <p className="text-xs text-muted-foreground max-w-xs">{helperText}</p>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={copyLink}
              className="inline-flex items-center justify-center gap-1 flex-1"
            >
              <Copy className="h-4 w-4" />
              Copy Link
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadQr}
              className="inline-flex items-center justify-center gap-1 flex-1"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Quick pay link unavailable for this account.
        </p>
      )}
    </div>
  );
}

export default QuickPayQRCode;
